import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/components/ui/dialog';
import React, { useEffect, useMemo, useState } from 'react';
import { useKBContext } from 'src/provider/KnowledgeBaseProvider';
import {
  useLazyGetKnowledgeBaseFileByIdQuery,
  useLazyGetKnowledgeBaseFileContentQuery,
  useUpdateKnowledgeBaseFileMutation,
} from 'src/services';
import { useParams } from 'react-router-dom';
import { Skeleton } from 'src/components/ui/skeleton';
import { Controller, useForm } from 'react-hook-form';
import Input from 'src/components/Input/Input';
import { Button } from 'src/components/ui/button';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { useLoader } from 'src/provider/LoaderProvider';
import { useNotification } from 'src/hooks/useNotification';
import { getFileExtension } from 'src/utils/fileUtils';
import { useAppSelector } from 'src/store';
import { GOOGLE_URL } from 'src/constants/urls';
import { PlateEditor } from 'src/components/PlateEditor/PlateEditor';

export const CurrentKnowledgeBaseEditFilePopup = () => {
  const { selectedItem, isEditPopupOpen, setIsEditPopupOpen, setSelectedItem } = useKBContext();

  if (!selectedItem) {
    return null;
  }

  const itemExt = getFileExtension(selectedItem.name);

  const isFile = selectedItem?.type === 'file';
  const isMd = selectedItem?.name?.endsWith('.md');
  const isFaq = selectedItem?.type === 'FAQ';
  const isWebsite = selectedItem?.type === 'website' && Boolean(selectedItem?.collector_id);
  const isReadonly = isWebsite;
  const portal = useMemo(() => document.getElementById('popup-root'), []);
  const { id } = useParams();
  const loader = useLoader();
  const notification = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [editorInitialValue, setEditorInitialValue] = useState('');
  const [errorMessage, setErrorMessage] = useState(false);
  const { managedAccountId } = useAppSelector((s) => s.companySlices);
  const [fetchFile] = useLazyGetKnowledgeBaseFileByIdQuery();
  const [fetchContent] = useLazyGetKnowledgeBaseFileContentQuery();
  const { handleSubmit, trigger, control, setValue, setError, reset, clearErrors } = useForm<any>({
    defaultValues: async () => ({
      name: selectedItem?.name || '',
    }),
    resolver: yupResolver(
      object({
        name: stringScheme({ required: true, minLength: 3, noslash: true })
          .label('File Name')
          .test('extension', `File Extension should be .${itemExt}`, (value) => {
            const ext = getFileExtension(value);

            return selectedItem?.type === 'file' && itemExt ? itemExt === ext : true;
          }),
        text: stringScheme({ required: true, minLength: 3 }).label('Content/Answer'),
      }),
    ),
  });

  const [
    updateFile,
    { isLoading: isLoadingUpdateKnowledgeBaseFile, isSuccess: isSuccessUpdateKnowledgeBaseFile },
  ] = useUpdateKnowledgeBaseFileMutation();

  const onOpenChange = (isOpen) => {
    setIsEditPopupOpen(isOpen);
  };

  useEffect(() => {
    if (isEditPopupOpen && (isFile || isFaq || isWebsite)) {
      setValue('name', selectedItem?.name || '');

      fetchFile({ id, file_id: selectedItem.id }).then((res: any) => {
        const url = res?.data?.download_url || null;
        setDownloadUrl(url);
        setIsLoading(Boolean(url));
      });
    }

    if (!isEditPopupOpen) {
      setSelectedItem(null);
      setFileContent('');
      setIsLoading(true);
      setDownloadUrl(null);
      setErrorMessage(false);
      reset();
    }
  }, [isEditPopupOpen]);

  useEffect(() => {
    if (!downloadUrl) return;

    const isGoogleStorage = downloadUrl.startsWith(GOOGLE_URL.STORAGE);

    fetchContent({
      url: `${downloadUrl}${isGoogleStorage ? '' : managedAccountId ? `?account=${managedAccountId}` : ''}`,
    }).then((result: any) => {
      setFileContent(result.data);
      setIsLoading(false);
    });
  }, [downloadUrl]);

  useEffect(() => {
    try {
      if (fileContent) {
        if (isFaq || isMd || isWebsite) {
          setEditorInitialValue(fileContent);
        } else {
          setValue('text', fileContent);
        }
      } else if (isFaq || isMd || isWebsite) {
        setEditorInitialValue('');
      }
    } catch (e) {
      setErrorMessage(true);
    }
  }, [fileContent]);

  useEffect(() => {
    if (isLoadingUpdateKnowledgeBaseFile) {
      setIsLoading(true);
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingUpdateKnowledgeBaseFile]);

  useEffect(() => {
    if (isSuccessUpdateKnowledgeBaseFile) {
      notification.success('Updated Successfully.');
      onOpenChange(false);
    }
  }, [isSuccessUpdateKnowledgeBaseFile]);

  const submit = ({ name, text }) => {
    if (text.replace(/[\s\u200B-\u200D\uFEFF]/g, '').trim().length <= 1) {
      setError('text', { message: 'Field Required' });
      return;
    }

    setIsLoading(true);
    const body: { name: string; text: string } = {
      name: isFaq ? `${name.replace(/^\/+|\/+$/g, '').trim()}.faq` : name,
      text: '',
    };

    if (!errorMessage && downloadUrl && text) {
      body.text = text;
    }

    updateFile({ knowledgeBaseId: id, fileId: selectedItem.id, body });
  };

  return (
    selectedItem && (
      <>
        <Dialog open={isEditPopupOpen} onOpenChange={onOpenChange}>
          <DialogContent container={portal} className={'relative overflow-auto'}>
            <DialogHeader>
              <DialogTitle>{isReadonly ? 'View' : 'Edit'}</DialogTitle>
            </DialogHeader>
            {isLoading ? (
              <div className="flex md:gap-10 gap-4 w-full items-center">
                <div className="flex flex-col items-center w-full gap-5">
                  <Skeleton className="w-full min-h-[50px]" />
                  <Skeleton className="w-full min-h-[200px]" />
                </div>
              </div>
            ) : (
              <div className="grid gap-4 py-4">
                <form id={'edit-file-form'} onSubmit={handleSubmit(submit)}>
                  <div className={'input-container flex flex-col gap-[3px]'}>
                    <Controller
                      control={control}
                      name="name"
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          autoFocus
                          type={'text'}
                          readonly={isReadonly}
                          label={isFaq ? 'Topic/Question' : isWebsite ? 'Title' : 'File Name'}
                          placeholder={
                            isFaq ? 'Enter a question or FAQ name here' : selectedItem.name
                          }
                          invalidMessage={error?.message}
                          value={value}
                          isValid={!error?.message}
                          onChange={onChange}
                        />
                      )}
                    />
                  </div>
                </form>
                {errorMessage ? (
                  <p className={'text-center text-base text-ebony font-medium'}>
                    Cannot open file
                    <br /> Error: File includes a code snippet with unexpected characters "{`{,}`}",
                    etc.
                  </p>
                ) : !downloadUrl ? null : isMd || isFaq || isWebsite ? (
                  <Controller
                    control={control}
                    name="text"
                    render={({ field: { onChange }, fieldState: { error } }) => (
                      <PlateEditor
                        label={isFaq ? 'Content/Answer' : 'Text'}
                        initialValue={editorInitialValue}
                        onChange={onChange}
                        error={error}
                      />
                    )}
                  />
                ) : (
                  <Controller
                    control={control}
                    name="text"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <div>
                        <Input
                          type={'textarea'}
                          label={'Text:'}
                          onChange={onChange}
                          value={value}
                          isValid={!error?.message}
                          invalidMessage={error?.message}
                          textareaRows={16}
                          placeholder={'Some text here...'}
                          textareaFieldSizing={'fixed'}
                          className={'max-h-[450px]'}
                        />
                      </div>
                    )}
                  />
                )}

                <div className="flex justify-center mt-5">
                  <div className="flex gap-2.5">
                    <Button
                      variant={isReadonly ? 'default' : 'outline'}
                      type="button"
                      className="min-w-24"
                      onClick={() => onOpenChange(false)}
                    >
                      {isReadonly ? 'Close' : 'Cancel'}
                    </Button>
                    {!isWebsite && (
                      <Button form={'edit-file-form'} className="min-w-24">
                        <span>Update</span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>
    )
  );
};
